/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React from 'react';
import PropTypes from 'prop-types';
import Control from '../Control/Control';
import Label from '../Label/Label';
import $ from '../Util/Util';
import Datetime from '../Util/Datetime';
import CalendarIcon from './calendar-range.png';
import LeftNavigatorIcon from './baseline_keyboard_arrow_left_white_24dp.png';
import RightNavigatorIcon from './baseline_keyboard_arrow_right_white_24dp.png';
import './Datebox.css';

/**
 * Represents the datebox component.
 */

export default class Datebox extends Control {

    /** Label positions. */
    static get LABELPOSITION() { return {
        TOP:   'top',
        LEFT:  'left'
    };}

    /**
     * Properties.
     */

    static propTypes = {
        label:            PropTypes.string,
        labelwidth:       PropTypes.number,
        labelposition:    PropTypes.string,
        disabled:         PropTypes.bool,
        isrequired:       PropTypes.bool,
        validateontyping: PropTypes.bool,
        validateonblur:   PropTypes.bool,
        requiredmessage:  PropTypes.string,
        placeholder:      PropTypes.string,
        tooltip:          PropTypes.string,
        firstdayofweek:   PropTypes.number,
        dateformat:       PropTypes.string,
        todaytooltip:     PropTypes.string,
        comment:          PropTypes.string,
        onblur:           PropTypes.func,
        onchange:         PropTypes.func        
    }

    /**
     * Default property values.
     */

    static defaultProps = {
        requiredmessage:    'Mandatory field',
        invalidmessage:     'Invalid date',
        firstdayofweek:     0,
        dateformat:         'yyyy.mm.dd',
        todaytooltip:       'Show today'
    }

    /**
     * Default state.
     */

    state = { 
        error:              null, 
        showerror:          false,
        showcalendar:       false,
        calendardate:       null
    }
   
    /**
     * Sets the focus to the datebox.
     */
    
    setFocus = () => {
        this.input.focus();
    }

    /**
     * Gets the value of the datebox.
     *
     * @returns {String}  value   value of the datebox
     */

    getValue = () => {
      
        // Getting the value of the datebox

        return Datetime.parseDate(this.input.value, this.props.dateformat);
    }

    /**
     * Sets the value of the datebox.
     *
     * @param {String}  value   value of the datebox
     */

    setValue = (value) => {
      
        // Setting the value of the datebox

        this.input.value = new Datetime(value).format(this.props.dateformat);
        this.validate();
    }

    /**
     * Clears the value and the error message of the datebox.
     */

    clearValue = () => {
      
        // Clearing the value

        this.input.value = '';
        this.clearError();
    }

    /**
     * Clears the error message of the datebox.
     */

    clearError = () => {
      
        // Clearing the error

        this.setError();
    }

    /**
     * Sets the error message of the datebox.
     *
     * @param {String}  message   error message that has to show
     */

    setError = (message) => {
      
        // Does the error message specified?

        if (message) {

            // Showing the error message

            this.setState({ error: message }, () => {
                setTimeout(() => {
                    this.setState({ showerror: true });
                }, 20);
            });
        } else {

            // Clearing the error message

            this.setState({ showerror: false }, () => {
                setTimeout(() => {
                    this.setState({ error: null });
                }, 250);
            });
        }
    }

    /**
     * Checks that the value of the datebox is empty or not.
     *
     * @param     {String}  value   value that has to check (optional)
     * @returns   {Boolean} empty   specified value or the value of the datebox is
     *                              empty or not
     */

    isEmpty = (value) => {
        
        // Getting the value

        let v = (value || this.input.value);

        // Checking the value

        return (v === null || v === undefined) ? true : v.toString().trim() === '';
    }

    /**
     * Validates the datebox.
     * 
     * @param     {String}  value   value that has to check (optional)
     * @returns   {Boolean} valid   specified value or the value of the datebox is
     *                              valid or not
     */

    validate = (value) => {

        // Does the value of the datebox valid?

        if (!(this.props.isrequired && this.isEmpty(value))) {
            if ((!this.props.isrequired && this.isEmpty(value)) || 
            (Datetime.isValidDate(value || this.input.value, this.props.dateformat))) {
                if (!value) {
                    this.clearError();
                }

                return true;
            } else {

                // Datebox is invalid

                if (!value) {
                    this.setError(this.props.invalidmessage);
                }

                return false;
            }
        }

        // Datebox is invalid

        if (!value) {
            this.setError(this.props.requiredmessage);
        }

        return false;
    }

    /**
     * Validates the datebox on typing.
     */

    _validateOnTyping = () => {

        // Validating

        if (this.props.validateontyping) {
            this.validate();
        }

        // Firing the "onchange" event

        if (this.props.onchange) {
            this.props.onchange();
        }
    }

    /**
     * Validates the datebox on blur.
     */

    _validateOnBlur = () => {
        
        // Validating
        
        if (this.props.validateonblur) {
            this.validate();
        }

        // Firing the "onblur" event

        if (this.props.onblur) {
            this.props.onblur();
        }
    }

    /**
     * Fires the "onEnter" event if the user presses the ENTER key.
     */

    _isEnter = (event) => {
        if ((event.keyCode ? event.keyCode : event.which) === 13 && this.props.onEnter) {
            this.props.onEnter();
        }
    }

    /**
     * Gets the style of the datebox.
     * 
     * @return  {Object}    style   style of the datebox
     */

    getStyle = () => {

        // Preparing

        let style = this._getStyle();
        
        // Adding style attributes
        
        style.marginBottom = (this.props.bottomgap || 0) + 16 + 'px';

        // Returns the style

        return style;
    }

    /**
     * Shows the calendar.
     * 
     * @param   {Object}    event       event object
     */

    _showCalendar = (event) => {

        // Does the calendar visible?

        if (this.state.showcalendar) {
            event.preventDefault();
            event.stopPropagation();
        } else {

            // Showing the calendar

            this.clearError();
            let date = Datetime.parseDate(this.input.value, this.props.dateformat);
            this._refreshCalendar(date, date, date === null);
            window.document.body.addEventListener('click', this._hideCalendar);
            this.setState({ showcalendar: true });
        }
    }

    /**
     * Hides the calendar.
     * 
     * @param   {Object}    event       event object
     */

    _hideCalendar = (event) => {

        // Does the calendar visible?

        if (this.state.showcalendar) {

            // Calendar closer

            let closeCalendar = () => {
                window.document.body.removeEventListener('click', this._hideCalendar);
                this.setState({ showcalendar: false });
            };

            if (event) {

                // Getting the clicked node

                let node = event.target;

                while (node && this.calendar !== node && this.calendarbutton !== node) {
                    node = node.parentNode;
                }

                // Closing the calendar

                if (!node) {
                    event.preventDefault();
                    event.stopPropagation();
                    closeCalendar();
                }
            } else {

                // Closing the calendar

                closeCalendar();
            }
        }
    }

    /**
     * Refreshes the content of the calendar.
     * 
     * @param   {Date}      date                date that has to show on the calendar page (Optional)
     * @param   {Date}      selected            selected date (Optional)
     * @param   {Boolean}   clearselection      clear the selected date
     */

    _refreshCalendar = (date, selected, clearselection) => {

        // Preparing

        let now = new Date();

        if (!date) {
            date = now;
        }

        // Generating day names

        let daynames = [];

        for (let i = this.props.firstdayofweek; i < this.props.firstdayofweek + 7; i++) {
            daynames.push($.capitalizeFirstLetter(Datetime.SHORTDAY.hu[i > 6 ? i - 7: i]));
        }

        // Generating the data for the content

        let weeks = [];

        for (let i = 0; i < 6; i++) {
            let days = [];
            let firstdate = new Date(date.getFullYear(), date.getMonth(), 1);
            let fd = firstdate.getDay() === 0 ? 7 : firstdate.getDay();

            let d = new Date(firstdate);
            d.setDate(d.getDate() + ((fd - this.props.firstdayofweek) * -1));
            d.setDate(d.getDate() + (i * 7));

            if (i > 0 && d.getMonth() !== date.getMonth()) {
                break;
            }

            days.push({ 
                year:           d.getFullYear(), 
                month:          d.getMonth(), 
                day:            d.getDate(), 
                isforeignmonth: d.getMonth() !== date.getMonth(),
                iscurrentday:   new Datetime(d).isEqual(now), 
                isselectedday:  selected && !clearselection ? new Datetime(d).isEqual(selected) : 
                                (clearselection || this.state.calendardate === null || 
                                this.state.calendardate.selected === null ? false : 
                                new Datetime(d).isEqual(this.state.calendardate.selected))
            });
            
            for (let i = 1; i < 7; i++) {
                d.setDate(d.getDate() + 1);
                days.push( {
                    year:           d.getFullYear(), 
                    month:          d.getMonth(), 
                    day:            d.getDate(),
                    isforeignmonth: d.getMonth() !== date.getMonth(),
                    iscurrentday:   new Datetime(d).isEqual(now),
                    isselectedday:  selected && !clearselection ? new Datetime(d).isEqual(selected) : 
                                    (clearselection || this.state.calendardate === null || 
                                    this.state.calendardate.selected === null ? false :
                                    new Datetime(d).isEqual(this.state.calendardate.selected))
                });
            }

            weeks.push(days);
        }

        // Refreshing

        this.setState({ calendardate: {
            date:       new Datetime(date),
            year:       date.getFullYear(),
            month:      date.getMonth(),
            monthtext:  $.capitalizeFirstLetter(Datetime.MONTH.hu[date.getMonth()]),
            daynames:   daynames,
            weeks:      weeks,
            selected:   selected && !clearselection ? selected : 
                        ((clearselection || this.state.calendardate === null) ? null : 
                        this.state.calendardate.selected)
        }});
    }

    /**
     * Jump to previous calendar page.
     */

    _calendarLeft = () => {
        this._refreshCalendar(this.state.calendardate.date.addMonth(-1));
    }

    /**
     * Jump to next calendar page.
     */
    
    _calendarRight = () => {
        this._refreshCalendar(this.state.calendardate.date.addMonth(1));
    }

    /**
     * Jump to today date.
     */

    _today = () => {
        this._refreshCalendar();
    }

    /**
     * Selects a day from the calendar.
     * 
     * @param   {Object}    event       event object
     */

    _selectDay = (event) => {

        // Getting the selected day

        let calendardate = this.state.calendardate;
        calendardate.selected = new Date(
            parseInt(event.target.getAttribute('data-year'), 10),
            parseInt(event.target.getAttribute('data-month'), 10),
            parseInt(event.target.getAttribute('data-day'), 10)
        );

        // Refreshing the state and the calendar
        
        this.setState({calendardate: calendardate}, () => {
            this._refreshCalendar(this.state.calendardate.date.toDate());
            this._hideCalendar();
            this.setValue(calendardate.selected);

            // Firing the "onchange" event

            if (this.props.onchange) {
                this.props.onchange();
            }
        });
    }

    /**
     * Datebox renderer.
     */

    render() {

        // Setting default label position

        let labelposition = this.props.labelposition || Datebox.LABELPOSITION.LEFT;

        // Rendering

        return (

            /* Datebox container */

            <div className="datebox" style={this.getStyle()} title={this.props.tooltip}>
                <div className={`${this.props.label && labelposition === Datebox.LABELPOSITION.LEFT ? 'horizontal' : 'vertical'}`}>

                    {/* Label */}
                
                    { this.props.label && <div><Label text={this.props.label} topgap={labelposition === Datebox.LABELPOSITION.TOP ? 0 : 6} rightgap={this.props.labelwidth || labelposition === Datebox.LABELPOSITION.TOP ? 0 : 8} width={this.props.labelwidth} isrequired={this.props.isrequired} requiredmessage={this.props.requiredmessage} /></div>}
                    
                    {/* Datebox container */}

                    <div className="datebox-container">
                        <div className="horizontal">

                            {/* Inputbox */}

                            <input 
                                ref={c => {this.input = c;}} 
                                type="text"
                                disabled={this.props.disabled}
                                placeholder={this.props.placeholder}
                                className={`datebox-input ${this.state.error ? 'datebox-input-invalid' : ''} datebox-input-${this.props.disabled ? 'disabled' : 'enabled'}`}
                                maxLength="10"
                                onFocus={(event) => {event.target.select();}}
                                onKeyDown={this._isEnter}
                                onKeyUp={this._validateOnTyping}
                                onBlur={this._validateOnBlur}
                            />

                            {/* Calendar button */}

                            <img ref={c => {this.calendarbutton = c;}} alt="Calendar" src={CalendarIcon} className="datebox-calendaricon" onClick={this._showCalendar} />
                        </div>

                        {/* Calendar */}

                        { this.state.showcalendar && <div ref={c => {this.calendar = c;}} className="datebox-calendar">
                            <div className="datebox-calendar-header horizontal center">
                                <div><img alt="Navigator" className="datebox-calendar-header-navigatoricon" src={LeftNavigatorIcon} onClick={this._calendarLeft} /></div>
                                <div className="horizontal flex center center-justified" title={this.props.todaytooltip} onClick={this._today}>
                                    <div className="datebox-calendar-header-year">{this.state.calendardate.year}</div>
                                    <div className="datebox-calendar-header-month">{this.state.calendardate.monthtext}</div>
                                </div>
                                <div><img alt="Navigator" className="datebox-calendar-header-navigatoricon" src={RightNavigatorIcon} onClick={this._calendarRight} /></div>
                            </div>
                            <div className="horizontal">
                                { this.state.calendardate.daynames.map((item, index) =>
                                    <div key={index} className="datebox-calendar-dayname flex horizontal center-justified">{item}</div>
                                )}
                            </div>
                            { this.state.calendardate.weeks.map((item, index) =>
                                <div key={index} className="horizontal">
                                    { item.map((d, index) =>
                                        <div key={index} data-year={d.year} data-month={d.month} data-day={d.day} 
                                        className={`datebox-calendar-week-day flex horizontal center-justified ${d.isselectedday ? 'datebox-calendar-week-selectedday' : (d.iscurrentday ? 'datebox-calendar-week-currentday' : ('datebox-calendar-week-day-unselected ' + (d.isforeignmonth ? 'datebox-calendar-week-foreignmonth' : '')))}`} onClick={this._selectDay}>{d.day}</div>
                                    )}
                                </div>
                            )}
                        </div> }

                        {/* Comment */}

                        { this.props.comment && <div className="datebox-comment">{this.props.comment}</div> }

                        {/* Error message */}

                        {this.state.error && (<div className={`datebox-error ${this.state.showerror ? 'datebox-error-show' : ''}`} style={{ top: (this.props.comment ? -4 : 8) + 'px'}}>
                            <div className="datebox-error-container">
                                <div className="datebox-error-content">{this.state.error}</div>
                            </div>
                        </div>)}
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Datebox/Datebox.js